'use client';

import dynamic from 'next/dynamic';
import React, { type HTMLAttributes, type ReactNode, useEffect, useRef, useState } from 'react';
import { css } from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { IconCircleInfoSolid } from '@/app/common/icons/libraryIcons';

import { Alert, InfoContainer } from '@/app/common/designSystemTypedComponents.ts';

/* HOOKS */
import { useIsDesktopOrGreater, useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useTranslation } from '@/app/components/translations/Translation';

/* COMPONENTS */
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import RoomPanelSkeleton, { RoomPanelSkeletonMobile } from '@/app/components/hotel-detail/rooms/RoomPanelSkeleton';
import { PayAtHotelBanner } from '@/app/components/hotel-detail/PayAtHotelBanner';

import customCss from '@/app/components/hotel-detail/css/customCss';
import { RoomPanel } from '@/app/components/hotel-detail/rooms/RoomPanel';
import { SubNavigation } from '@/app/components/hotel-detail/SubNavigation';
import { pageSectionIds } from '@/app/components/hotel-detail/common/consts';
import SoldOutAlternativeDates from '@/app/components/hotel-detail/SoldOutAlternativeDates';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { MealPlanFilterMessage } from '@/app/components/hotel-detail/rooms/MealPlanFilterMessage';
import { useTrackHotelShown } from '@/app/components/hotel-detail/hooks/useTrackHotelShown';
import BannerMyPrice from '@/app/components/hotel-detail/BannerMyPrice';
import BannerLoyalty from '@/app/components/search/components/Banners/LoyaltyBanner';
import { BannerLoyaltyEnrolled } from '@/app/components/hotel-detail/BannerLoyaltyEnrolled';
import { shouldShowLoyaltyBanner, shouldShowLoyaltyEnrolledBanner } from '@/app/common/Loyalty/utils';
import { LOYALTY_CONTEXT, LOYALTY_POSITION } from '@/app/common/starTrack/events/utils/eventsUtils';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import useTransportsFetch from './travel-api/transportsFetch';
import { RoomPanelModalProvider } from './rooms/RoomPanelModalProvider';
import { SoldOutMessage } from './SoldOutMessage';
import { PackageDetails, PackageDetailsContainer } from './PackageDetails';
import PriceCalendar from './PriceCalendar';
import BannerDeferredPayments from './BannerDeferredPayments';

// const TransportLayout = dynamic(() => import(/* webpackChunkName: "TransportLayout" */ './TransportLayout'), { ssr: false });

const RoomPanelModal = dynamic(() => import(/* webpackChunkName: "RoomPanelModal" */ './rooms/RoomPanelModal'), {
  ssr: false,
});

type HotelPageRoomSectionProps = HTMLAttributes<HTMLElement>;

const BannerCss = css`
  margin-top: 16px;

  @media ${customCss.breakpoints.sm} {
    margin: 16px 24px 0;
  }

  @media ${customCss.breakpoints.lg} {
    margin: 16px 24px 0;
  }

  @media ${customCss.breakpoints.lgPlus} {
    margin: 16px 40px 0;
  }

  @media ${customCss.breakpoints.xl} {
    margin: 16px 0 0;
  }
`;

const LoyaltyBannerCss = css`
  ${BannerCss};
  width: auto;
  border-radius: 0;

  @media ${customCss.breakpoints.sm} {
    border-radius: 8px;
  }
`;

export const HotelPageRoomSection = (props: HotelPageRoomSectionProps) => {
  const { commonData, localization } = usePageWrapperData();
  const urlSearchParams = useUrlSearchParams();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { t } = useTranslation();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { offersData, isSoldout } = useOffersContext();
  const { nights, isPastCheckin } = useUrlSearchParams();
  const [maxLoyaltyDiscount, setMaxLoyaltyDiscount] = useState<number | undefined>(undefined);

  const isLoggedIn = commonData.authResponse?.loggedIn;
  const isApp = commonData?.isApp;
  const hasLoyaltyOffers = offersData?.hasLoyaltyOffers;

  const showLoyaltyBanner = shouldShowLoyaltyBanner(commonData, localization, isApp);
  const showLoyaltyEnrolledBanner = shouldShowLoyaltyEnrolledBanner(
    commonData,
    localization,
    !!hasLoyaltyOffers,
    isApp
  );

  // Prefetch alternative transports
  useTransportsFetch();

  useTrackHotelShown();

  const ref = useRef<HTMLDivElement | null>(null);

  const showPriceCalendar = !urlSearchParams.isPastCheckin && nights < 15; // More than 14 nights is not supported by the API
  const showSoldOutMessage = isSoldout && !urlSearchParams.errorType && showPriceCalendar;
  const showSoldoutAlternativeDates = isSoldout && !urlSearchParams.errorType && !showPriceCalendar;

  let preSelectedRoom;
  if (typeof window !== 'undefined') {
    const currentUrlParams = new URLSearchParams(window.location.search);
    preSelectedRoom = offersData?.rooms?.find((room) =>
      currentUrlParams.get(URLParameters.MODAL)?.includes(room.roomOfferId)
    );
  }

  useEffect(() => {
    if (!ref.current) return;
    const currentUrlParams = new URLSearchParams(window.location.search);
    if (currentUrlParams.get('sectionId') === 'rooms') {
      ref.current.scrollIntoView();
    }
  }, [ref.current]);

  useEffect(() => {
    if (showLoyaltyEnrolledBanner && offersData?.rooms && offersData.rooms.length > 0) {
      const loyaltyDiscounts = offersData.rooms.flatMap((room) =>
        room.configurations
          .filter((mealPlan) => mealPlan.ratePriceType === 'LOYALTY' && mealPlan.discountPercentage)
          .map((mealPlan) => mealPlan.discountPercentage!)
      );

      const maxDiscount = loyaltyDiscounts.length > 0 ? Math.max(...loyaltyDiscounts) : undefined;
      setMaxLoyaltyDiscount(maxDiscount);
    }
  }, [showLoyaltyEnrolledBanner, offersData?.rooms]);

  return (
    <div {...props}>
      {showLoyaltyEnrolledBanner && maxLoyaltyDiscount ? (
        <BannerLoyaltyEnrolled
          discountPercentage={maxLoyaltyDiscount}
          loyaltyContext={LOYALTY_CONTEXT.DETAIL}
          loyaltyPosition={LOYALTY_POSITION.TOP}
          isLoggedIn={isLoggedIn}
          css={LoyaltyBannerCss}
        />
      ) : showLoyaltyBanner ? (
        <BannerLoyalty
          css={LoyaltyBannerCss}
          loyaltyContext={LOYALTY_CONTEXT.DETAIL}
          loyaltyPosition={LOYALTY_POSITION.TOP}
          isLoggedIn={isLoggedIn}
        />
      ) : !isSoldout && offersData?.showMyPriceBanner ? (
        <BannerMyPrice css={BannerCss} />
      ) : !isSoldout &&
        !localization?.configValuesApp?.splitPaymentsEnabled &&
        (offersData?.showDeferredPayments || offersData?.showSmallAmountDeferredPayments) ? (
        <BannerDeferredPayments
          smallAmountDeposit={offersData?.showSmallAmountDeferredPayments ?? false}
          css={BannerCss}
        />
      ) : null}

      <PackageDetails
        css={
          !showPriceCalendar
            ? `${PackageDetailsContainer} {
                  @media ${customCss.breakpoints.sm} {    
                      border-radius: 8px;
              }}`
            : undefined
        }
      />
      {showPriceCalendar && !showSoldoutAlternativeDates && (
        <PriceCalendar
          css={`
            @media ${customCss.breakpoints.sm} {
              border-radius: 0 0 8px 8px;
            }
          `}
        />
      )}

      {showSoldOutMessage ? <SoldOutMessage /> : undefined}

      {!isSoldout || showSoldoutAlternativeDates ? (
        <HotelPageContentSection
          sectionRef={ref}
          title={
            isMobile && !showSoldOutMessage && !showSoldoutAlternativeDates
              ? hotelStaticData?.accommodationType === 'HOTELS'
                ? t('vacation.select.section.rooms.title')
                : t('vacation.select.section.accommodation.title')
              : undefined
          }
          css={`
            ${
              showSoldoutAlternativeDates
                ? `
            background-color: white;           

            @media ${customCss.breakpoints.sm} {
              background-color: inherit;
            }
              `
                : undefined
            }

            @media ${customCss.breakpoints.sm} {
              margin-top: 16px;
            }
          `}
          subTitle={
            showSoldoutAlternativeDates ? (
              <div
                css={css`
                  display: flex;
                  width: 100%;
                  flex-wrap: wrap;
                `}
              >
                {showSoldoutAlternativeDates ? (
                  <div
                    css={css`
                      display: flex;
                      width: 100%;
                      border-radius: 8px;
                      background: white;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex-wrap: wrap;
                        width: 100%;
                        padding: 0;
                        @media ${customCss.breakpoints.sm} {
                          padding: 24px;
                        }
                      `}
                    >
                      <SoldOutAlternativeDates />
                    </div>
                  </div>
                ) : undefined}
              </div>
            ) : undefined
          }
        >
          {!isPastCheckin ? (
            <RoomPanelModalProvider preSelectedRoom={preSelectedRoom}>
              <RoomsList />
              {offersData?.rooms && offersData.rooms.length > 0 ? <RoomPanelModal /> : undefined}
            </RoomPanelModalProvider>
          ) : null}
        </HotelPageContentSection>
      ) : undefined}
    </div>
  );
};

const RoomsList = () => {
  const { offersData, isLoadingRooms, isSoldout } = useOffersContext();
  const { commonData } = usePageWrapperData();

  const isDesktop = useIsDesktopOrGreater(commonData.isDesktopDevice);

  const { t } = useTranslation();
  const urlSearchParams = useUrlSearchParams();

  const showSkeletons = isLoadingRooms;

  const rooms = offersData?.rooms;
  const showRooms = rooms && rooms.length > 0 && !isLoadingRooms;
  const isNonAvailable = isSoldout;

  const noRoomImages = rooms?.reduce((acc, cur) => acc && cur.images.length === 0, true);

  return (
    <RoomsListContainer>
      {(offersData?.payAtHotelMealPlansCount ?? 0) > 0 ? (
        <PayAtHotelBanner
          css={`
            margin-bottom: 1.5rem;
          `}
        />
      ) : null}
      {showSkeletons || (!showRooms && !isNonAvailable) ? (
        commonData.isMobileDevice || commonData.isTabletDevice ? (
          <RoomPanelSkeletonMobile />
        ) : (
          <RoomPanelSkeleton />
        )
      ) : null}
      {/* {showRooms ? <TransportLayout preload={true} /> : null} */}
      {showRooms && offersData?.roomsFiltered ? <MealPlanFilterMessage /> : undefined}
      {showRooms ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            row-gap: 16px;

            @media ${customCss.breakpoints.sm} {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            }

            @media ${customCss.breakpoints.lgPlus} {
              display: flex;
              row-gap: 16px;
              border-radius: 8px;
            }
          `}
        >
          {offersData.lessRoomsIndex === 0 ? (
            <Alert
              variant="warning"
              icon={IconCircleInfoSolid}
              hasBorder={!isDesktop}
              message={t('vacation.select.room.multiple.alternative.error.banner', {
                numPax: urlSearchParams.totalGuests.toString(),
              })}
              css={`
                grid-column: span 2;
              `}
            />
          ) : null}

          <RoomsListGroup
            rooms={(offersData.lessRoomsIndex ?? 0) <= 0 ? rooms : rooms.slice(0, offersData.lessRoomsIndex)}
            noRoomImages={noRoomImages}
            showBestValue={!urlSearchParams.accommodationOnly}
          />
          {(offersData?.lessRoomsIndex ?? 0) > 0 ? (
            <>
              <InfoContainer
                type="specific"
                hasBorder={!isDesktop}
                icon={IconCircleInfoSolid}
                message={t(
                  `${
                    offersData?.lessRoomsIndex === rooms.length - 1
                      ? 'vacation.select.room.multiple.alternative.message.sing'
                      : 'vacation.select.room.multiple.alternative.message'
                  }`,
                  {
                    roomsNumber: urlSearchParams.totalRooms.toString(),
                    numberPerson: urlSearchParams.totalGuests.toString(),
                  }
                )}
                css={`
                  grid-column: span 2;
                `}
              />
              <RoomsListGroup
                rooms={rooms.slice(offersData.lessRoomsIndex)}
                noRoomImages={noRoomImages}
                showBestValue={false}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </RoomsListContainer>
  );
};

type RoomsListGroupProps = {
  rooms: RoomType[];
  noRoomImages: boolean | undefined;
  showBestValue: boolean;
};

function RoomsListGroup(props: RoomsListGroupProps) {
  const { rooms, noRoomImages, showBestValue } = props;

  return (
    <>
      {rooms.map((room, index) => (
        <RoomPanel
          room={room}
          key={`${room.name}-${room.roomOfferId}`}
          isBestValue={showBestValue && index === 0}
          noRoomImages={noRoomImages}
          css={css`
            border-radius: 8px;
            min-width: 0;
          `}
        />
      ))}
    </>
  );
}

function RoomsListContainer({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <MobileSubnavigation>{children}</MobileSubnavigation>
  ) : (
    // div needed to link the book buttons in the gallery, reviews, hero, etc.
    <div
      id={pageSectionIds.rooms}
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      {children}
    </div>
  );
}

function MobileSubnavigation({ children }: { children: ReactNode }) {
  const [ref, sectionInView] = useInView({ threshold: 0.02, triggerOnce: false });
  return (
    <div
      ref={ref}
      id={pageSectionIds.rooms}
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <SubNavigation hideCta={sectionInView} />
      {children}
    </div>
  );
}
